import React, { useEffect, useRef, useState } from 'react';
import {
  FolderLock, Upload, Trash2, Download, FileText, FileCheck2, Landmark, Users, Zap,
  IdCard, HeartPulse, Home as HomeIcon, FileQuestion,
} from 'lucide-react';
import { documentsApi } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const CATEGORIES = [
  { key: 'insurance_policy', label: 'Insurance policy', icon: FileText },
  { key: 'modelo_210', label: 'Modelo 210', icon: FileCheck2 },
  { key: 'title_deed', label: 'Title deed', icon: HomeIcon },
  { key: 'ibi', label: 'IBI', icon: Landmark },
  { key: 'community_fees', label: 'Community fees', icon: Users },
  { key: 'energy_certificate', label: 'Energy certificate', icon: Zap },
  { key: 'passport', label: 'Passport', icon: IdCard },
  { key: 'nie', label: 'NIE', icon: IdCard },
  { key: 'health_insurance', label: 'Health insurance', icon: HeartPulse },
  { key: 'other', label: 'Other', icon: FileQuestion },
];

const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

function humanSize(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('other');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const refresh = () => {
    documentsApi.list().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await documentsApi.upload(file, category, file.name);
      refresh();
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await documentsApi.remove(id);
    refresh();
  };

  const download = (id) => {
    const token = localStorage.getItem('esi_token');
    fetch(documentsApi.downloadUrl(id), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = items.find((d) => d.id === id)?.filename || 'file';
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const counts = CATEGORIES.reduce((acc, c) => ({
    ...acc,
    [c.key]: items.filter((d) => d.category === c.key).length,
  }), {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="eyebrow">Vault</span>
          <h1 className="mt-2 text-3xl font-bold text-[#0b1f3a]">Documents</h1>
          <p className="text-slate-600 text-sm mt-1">Your Spanish paperwork, all in one secure place.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-48">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button onClick={() => fileInput.current?.click()} disabled={uploading} className="btn-primary disabled:opacity-60">
            <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input ref={fileInput} type="file" hidden onChange={onFile} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
            <span className="icon-tile !w-9 !h-9"><c.icon size={16} /></span>
            <div>
              <p className="text-[13px] font-semibold text-[#0b1f3a] leading-tight">{c.label}</p>
              <p className="text-xs text-slate-500">{counts[c.key] || 0} files</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-slate-500 text-sm">Loading...</p>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <FolderLock size={32} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-700 font-semibold">Nothing here yet</p>
            <p className="text-slate-500 text-sm mt-1">Upload your first document above.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Category</th>
                <th className="text-left px-5 py-3">Source</th>
                <th className="text-left px-5 py-3">Size</th>
                <th className="text-left px-5 py-3">Uploaded</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((d) => {
                const Icon = (catMap[d.category] || catMap.other).icon;
                const isTax = d.source === 'tax';
                return (
                  <tr key={d.id}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="icon-tile !w-8 !h-8"><Icon size={14} /></span>
                        <span className="font-medium text-[#0b1f3a]">{d.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{(catMap[d.category] || catMap.other).label}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        isTax
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-[#eef4fb] text-[#0d3a8a]'
                      }`}>
                        {isTax ? 'EasySpanishTax' : 'Insurance'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{humanSize(d.size)}</td>
                    <td className="px-5 py-3 text-slate-600">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => download(d.id)} className="text-slate-500 hover:text-[#0d3a8a] mr-3" title="Download"><Download size={16} /></button>
                      {isTax ? (
                        <span title="Managed by EasySpanishTax">
                          <Trash2 size={16} className="text-slate-200 inline" />
                        </span>
                      ) : (
                        <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-rose-600" title="Delete"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
